/**
 * ApiService - Centralized AI API Management (Fixed Error Handling)
 * 
 * Handles all OpenAI API interactions with:
 * - Auto-initialization from multiple sources
 * - Rate limiting
 * - Retry logic
 * - Enhanced error handling
 * - Response caching
 * - Streaming support
 * 
 * @module services/ApiService
 */

import Config from '../core/Config.js';
import EventBus, { AppEvents } from '../core/EventBus.js';
import StorageService from './StorageService.js';
import Logger from '../utils/Logger.js';
import { parseAIResponse } from '../schemas/index.js';
import ControlPlane from './ControlPlaneService.js';

/**
 * API Service Configuration
 */
const API_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  timeout: 30000,
  cache: {
    enabled: true,
    ttl: 5 * 60 * 1000 // 5 minutes
  }
};

/**
 * Response cache entry
 */
class CacheEntry {
  constructor(data, ttl = API_CONFIG.cache.ttl) {
    this.data = data;
    this.expires = Date.now() + ttl;
  }
  
  isValid() {
    return Date.now() < this.expires;
  }
}

/**
 * Main API Service Class
 */
export class ApiService {
  constructor() {
    this.log = Logger.scope('ApiService');
    this.apiKey = null;
    this.cache = new Map();
    this.interceptors = [];
    this.activeRequests = new Map();
    this.requestCount = 0;
    this.lastRequestTime = 0;
    this.sessionId = null;
    this.initPromise = null; // Track initialization promise
    this.mockEnabled = false;
    
    // Rate limiting
    this.rateLimit = {
      requests: 60,
      window: 60000, // 1 minute
      queue: []
    };
    
    // Initialize asynchronously
    this.initPromise = this.initialize();
  }
  
  /**
   * Initialize the service with Promise-based key loading
   */
  async initialize() {
    this.log.info('🔧 ApiService initializing...');
    
    try {
      // Load API key with proper async chain
      this.apiKey = await this.loadApiKey();
      
      if (this.apiKey) {
        this.log.info('✅ API key loaded successfully');
      } else {
        this.log.warn('⚠️ No API key found during initialization');
        this.log.info('💡 Set your API key using: window.ApiService.setApiKey("sk-...")');
      }
      
      // Set up cache cleanup
      if (API_CONFIG.cache.enabled) {
        setInterval(() => this.cleanCache(), 60000);
      }
      
      this.log.info('✅ ApiService initialized');
      return true;
      
    } catch (error) {
      this.log.error('❌ ApiService initialization failed:', error);
      return false;
    }
  }
  
  /**
   * Load API key from all possible sources with Promise chain
   */
  async loadApiKey() {
    // If already loaded and valid, return it
    if (this.apiKey && this.validateKey(this.apiKey)) {
      return this.apiKey;
    }
    
    // Define sources in priority order
    const sources = [
      // Primary: StorageService
      async () => {
        try {
          // Wait a bit for StorageService to initialize if needed
          await new Promise(resolve => setTimeout(resolve, 50));
          return StorageService.get('apiKey');
        } catch {
          return null;
        }
      },
      // Secondary: Direct localStorage with JSON parsing
      async () => {
        try {
          const stored = localStorage.getItem('sip_apiKey');
          if (stored) {
            try {
              return JSON.parse(stored);
            } catch {
              return stored;
            }
          }
        } catch {
          return null;
        }
      },
      // Tertiary: Legacy localStorage key
      async () => {
        try {
          return localStorage.getItem('openai_api_key');
        } catch {
          return null;
        }
      },
      // Quaternary: Config object
      async () => {
        try {
          return Config.API?.OPENAI?.KEY;
        } catch {
          return null;
        }
      },
      // Last resort: Environment variable
      async () => {
        try {
          return import.meta.env?.VITE_OPENAI_API_KEY;
        } catch {
          return null;
        }
      }
    ];
    
    // Try each source in sequence
    for (const source of sources) {
      try {
        const key = await source();
        if (this.validateKey(key)) {
          this.apiKey = key;
          
          // Ensure it's saved in primary location for consistency
          if (!StorageService.get('apiKey')) {
            StorageService.set('apiKey', key);
          }
          
          this.log.info('✅ API key loaded from storage');
          return key;
        }
      } catch (error) {
        // Continue to next source
        this.log.debug('Source failed:', error);
      }
    }
    
    // No valid key found
    return null;
  }
  
  /**
   * Validate API key format
   */
  validateKey(key) {
    if (!key || typeof key !== 'string') return false;
    if (!key.startsWith('sk-')) return false;
    if (key.includes('your-') || key.includes('...')) return false;
    if (key.length < 20) return false; // Too short to be valid
    return true;
  }
  
  /**
   * Set API key with improved persistence
   */
  setApiKey(key) {
    if (!key || key === 'null' || key === 'undefined') {
      console.error('Invalid API key provided');
      return false;
    }
    
    // Clean the key
    key = key.trim().replace(/['"]/g, '');
    
    // Validate format
    if (!key.startsWith('sk-')) {
      console.error('Invalid API key format. Must start with "sk-"');
      return false;
    }
    
    this.apiKey = key;
    
    // Save to all storage locations for maximum compatibility
    try {
      // Primary storage
      StorageService.set('apiKey', key);
      
      // Legacy locations
      localStorage.setItem('openai_api_key', key);
      localStorage.setItem('sip_apiKey', JSON.stringify(key));
      
      this.log.info('✅ API key saved to all locations');
      
      // Emit event
      EventBus.emit(AppEvents.API_KEY_UPDATED, { hasKey: true });
      
      return true;
    } catch (error) {
      console.error('Failed to save API key:', error);
      return false;
    }
  }
  
  /**
   * Get user identifier for metadata
   */
  getUserId() {
    // Try to get from storage or generate
    let userId = localStorage.getItem('user_id');
    if (!userId) {
      userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('user_id', userId);
    }
    return userId;
  }

  /**
   * Get session identifier for metadata
   */
  getSessionId() {
    // Generate session ID if not exists
    if (!this.sessionId) {
      this.sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    return this.sessionId;
  }
  
  /**
   * Main API call method with better error handling
   * @param {string} prompt - The prompt to send
   * @param {Object} options - Call options
   * @returns {Promise<Object>} API response
   */
  async call(prompt, options = {}) {
    // Ensure initialization is complete
    if (this.initPromise) {
      await this.initPromise;
    }
    
    // Check for API key
    if (!this.apiKey) {
      // Try one more time with full async loading
      this.apiKey = await this.loadApiKey();
      if (!this.apiKey) {
        throw { 
          message: 'API key not configured',
          code: 'NO_API_KEY',
          content: 'Please set your API key in Settings'
        };
      }
    }
    
    // Default options
    const callOptions = {
      model: Config.API?.OPENAI?.MODEL || 'gpt-4',
      temperature: Config.API?.OPENAI?.TEMPERATURE || 0.7,
      maxTokens: Config.API?.OPENAI?.MAX_TOKENS || 2000,
      stream: false,
      useCache: API_CONFIG.cache.enabled,
      ...options
    };
    
    // Check cache
    if (callOptions.useCache && !callOptions.stream) {
      const cacheKey = this.getCacheKey(prompt, callOptions);
      const cached = this.cache.get(cacheKey);
      
      if (cached && cached.isValid()) {
        this.log.debug('📦 Returning cached response');
        return cached.data;
      }
    }
    
    // Apply rate limiting
    await this.rateLimitCheck();
    
    // Create request
    const requestId = ++this.requestCount;
    const requestData = {
      model: callOptions.model,
      messages: this.buildMessages(prompt, callOptions),
      temperature: callOptions.temperature,
      max_tokens: callOptions.maxTokens,
      stream: callOptions.stream,
      // Pass through response_format when provided to enable JSON mode
      ...(callOptions.response_format ? { response_format: callOptions.response_format } : {}),
      
      // 🆕 REQUIRED METADATA (simplified to match working example)
      store: true,
      metadata: {
        prompt_key: callOptions.promptKey || 'unknown',
        prompt_version: callOptions.promptVersion || '1.0.0',
        environment: Config.APP_ENV || 'production',
        app_version: Config.APP_VERSION || 'unknown'
      }
    };
    
    // Apply interceptors
    for (const interceptor of this.interceptors) {
      if (interceptor.request) {
        await interceptor.request(requestData);
      }
    }
    
    // Emit start event
    EventBus.emit(AppEvents.AI_REQUEST_START, { requestId, prompt });
    
    try {
      // Make request with retry logic
      const response = await this.makeRequestWithRetry(requestData, callOptions, requestId);
      
      // Process response
      const processed = await this.processResponse(response, callOptions);
      
      // Cache if applicable
      if (callOptions.useCache && !callOptions.stream) {
        const cacheKey = this.getCacheKey(prompt, callOptions);
        this.cache.set(cacheKey, new CacheEntry(processed));
      }
      
      // Apply response interceptors
      for (const interceptor of this.interceptors) {
        if (interceptor.response) {
          await interceptor.response(processed);
        }
      }
      
      // Emit success event
      EventBus.emit(AppEvents.AI_REQUEST_SUCCESS, { requestId, response: processed });
      
      return processed;
      
    } catch (error) {
      // Emit error event
      EventBus.emit(AppEvents.AI_REQUEST_ERROR, { requestId, error });
      throw this.enhanceError(error);
    } finally {
      // Clean up
      this.activeRequests.delete(requestId);
    }
  }
  
  /**
   * Build messages array for API
   */
  buildMessages(prompt, options) {
    const messages = [];
    
    // Add system message if provided
    if (options.systemRole) {
      messages.push({
        role: 'system',
        content: options.systemRole
      });
    }
    
    // Add conversation history if provided
    if (options.history && Array.isArray(options.history)) {
      messages.push(...options.history);
    }
    
    // Add user message
    messages.push({
      role: 'user',
      content: prompt
    });
    
    return messages;
  }
  
  /**
   * Make request with retry logic
   */
  async makeRequestWithRetry(requestData, options, requestId, attempt = 1) {
    // Mock harness (dev/test) — short-circuit network
    if (this.mockEnabled || this.shouldUseMock()) {
      return this.mockRequest(requestData, options);
    }
    try {
      // Store active request for potential cancellation
      const controller = new AbortController();
      this.activeRequests.set(requestId, controller);
      
      // Make request
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });
      
      // Check response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `API error: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      
      // Handle streaming
      if (options.stream) {
        return this.handleStreamingResponse(response, options);
      }
      
      return response.json();
      
    } catch (error) {
      // Check if we should retry
      if (attempt < API_CONFIG.maxRetries && this.shouldRetry(error)) {
        this.log.warn(`🔄 Retrying request (attempt ${attempt + 1}/${API_CONFIG.maxRetries})`);
        
        // Wait before retry
        await new Promise(resolve => 
          setTimeout(resolve, API_CONFIG.retryDelay * attempt)
        );
        
        return this.makeRequestWithRetry(requestData, options, requestId, attempt + 1);
      }
      
      throw error;
    }
  }

  /** Determine if mock LLM should be used */
  shouldUseMock() {
    try {
      if (typeof window !== 'undefined' && window.__USE_MOCK_LLM__) return (this.mockEnabled = true);
      if (typeof localStorage !== 'undefined' && localStorage.getItem('sip_mockLLM') === 'true') return (this.mockEnabled = true);
    } catch(_) {}
    try {
      if (import.meta.env?.VITE_USE_MOCK_LLM === 'true' || (Config.APP_ENV || '').toLowerCase() === 'test') {
        this.mockEnabled = true;
        return true;
      }
    } catch(_) {}
    return false;
  }

  /** Mocked request: returns deterministic responses based on prompt_key */
  async mockRequest(requestData, options) {
    const key = requestData?.metadata?.prompt_key || options?.promptKey || 'unknown';
    const model = options?.model || 'mock-model';
    let content = '';
    if (key === 'portfolioAnalysis') {
      content = 'This portfolio broadly supports the uploaded strategic pillars with reasonable coverage, though gaps remain in capability uplift and customer focus. Several initiatives overlap in scope; consolidating them would improve resource leverage.';
    } else if (key === 'initiativeTransfer') {
      const obj = {
        hasInitiative: true,
        confidence: 0.8,
        initiative: {
          id: `init_${Date.now()}`,
          title: 'Improve onboarding to reduce churn',
          description: 'Streamline onboarding and drive 30-day activation.',
          objective: 'Reduce churn by 15% through better early activation',
          keyResults: ['Increase 30-day activation to 60%', 'Reduce time-to-value to < 3 days'],
          strategicAlignment: 'Customer Centricity',
          priority: 'high',
          owner: 'TBD',
          status: 'draft'
        }
      };
      content = JSON.stringify(obj);
    } else if (key === 'riskAnalysis') {
      content = JSON.stringify({
        risks: [
          { category: 'implementation', description: 'Timeline slippage due to integration complexity', mitigation: 'Phase integrations and add buffer', severity: 'high' }
        ],
        summary: 'Key risks relate to integration and change adoption.'
      });
    } else if (key === 'initiativeSearch') {
      content = JSON.stringify({ relevantInitiatives: [], insights: ['No exact matches'], suggestions: ['Refine query'] });
    } else {
      content = 'OK';
    }
    return {
      id: 'mock-completion',
      object: 'chat.completion',
      created: Math.floor(Date.now() / 1000),
      model,
      choices: [{ index: 0, message: { role: 'assistant', content }, finish_reason: 'stop' }],
      usage: { prompt_tokens: 1, completion_tokens: content.length / 4 | 0, total_tokens: 1 + (content.length / 4 | 0) }
    };
  }
  
  /**
   * Handle streaming response
   */
  async handleStreamingResponse(response, options) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          
          if (data === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices[0]?.delta?.content || '';
            
            if (content) {
              fullContent += content;
              
              // Call onToken callback if provided
              if (options.onToken) {
                options.onToken(content, fullContent);
              }
            }
          } catch (e) {
            console.warn('Failed to parse streaming data:', e);
          }
        }
      }
    }
    
    return {
      choices: [{
        message: {
          content: fullContent,
          role: 'assistant'
        }
      }]
    };
  }
  
  /**
   * Process API response
   */
  async processResponse(response, options) {
    const content = response?.choices?.[0]?.message?.content || '';
    const legacyParsed = this.parseSpecialContent(content);
    let schemaParsed = null;
    try {
      schemaParsed = parseAIResponse(options?.promptKey, content);
    } catch (_) {
      schemaParsed = { ok: false, schema: null, data: null };
    }
    const processed = {
      content: content || '',
      parsed: { ...legacyParsed, schema: schemaParsed.schema, ok: schemaParsed.ok, data: schemaParsed.data, errors: schemaParsed.errors },
      usage: response?.usage,
      model: response?.model,
      timestamp: new Date().toISOString()
    };

    // Post usage to control plane if configured
    try {
      const totalTokens = Number(processed?.usage?.total_tokens || 0);
      if (totalTokens > 0) {
        ControlPlane.postUsageEvents([{ type: 'ai.tokens', quantity: totalTokens }]).catch(()=>{});
      }
      // In paid mode, check remaining and prompt for BYO when zero
      const summary = await ControlPlane.getUsageSummary('paid');
      if (summary && summary.tokens && Number(summary.tokens.remaining) <= 0) {
        // Soft prompt to add BYO key (local storage)
        try {
          if (typeof window !== 'undefined') {
            const want = confirm('Usage limit reached. Add your own OpenAI API key to continue?');
            if (want) {
              // Navigate to Settings tab if available
              try { window.App?.handleTabChange?.('settings'); } catch (_) {}
            }
          }
        } catch (_) {}
      }
    } catch (_) {}

    return processed;
  }
  
  /**
   * Parse special content from response
   */
  parseSpecialContent(content) {
    const result = {
      hasInitiative: false,
      initiative: null,
      metrics: [],
      actions: []
    };
    
    if (!content) return result;
    
    // Check for initiative pattern
    const initiativePattern = /(?:title|objective|goal):\s*(.+?)(?:\n|$)/i;
    const match = content.match(initiativePattern);
    
    if (match) {
      result.hasInitiative = true;
      result.initiative = {
        title: match[1].trim(),
        description: content
      };
    }
    
    // Extract metrics
    const metricPattern = /\d+%|\$[\d,]+|[\d,]+\s*(units|customers|days)/gi;
    result.metrics = content.match(metricPattern) || [];
    
    return result;
  }
  
  /**
   * Check if error should trigger retry
   */
  shouldRetry(error) {
    // Don't retry on 400 errors (bad request/invalid key)
    if (error.status === 400 || error.status === 401) {
      return false;
    }
    
    // Retry on network errors or specific status codes
    return error.message?.includes('network') ||
           error.message?.includes('timeout') ||
           error.status === 429 || // Rate limit
           error.status === 502 || // Bad gateway
           error.status === 503;   // Service unavailable
  }
  
  /**
   * Enhance error with helpful information
   */
  enhanceError(error) {
    // Create enhanced error with original message
    const enhanced = new Error(error.message || 'API request failed');
    enhanced.original = error;
    enhanced.status = error.status;
    
    // Add helpful context based on error type
    if (error.status === 401 || error.message?.includes('401') || error.message?.includes('Incorrect API key')) {
      enhanced.message = 'Invalid API key. Please check your OpenAI API key in Settings.';
      enhanced.code = 'INVALID_API_KEY';
    } else if (error.status === 400 || error.message?.includes('400')) {
      enhanced.message = 'Invalid request. Please check your API key is correctly formatted (should start with sk-).';
      enhanced.code = 'INVALID_REQUEST';
    } else if (error.status === 429 || error.message?.includes('429')) {
      enhanced.message = 'Rate limit exceeded. Please wait a moment and try again.';
      enhanced.code = 'RATE_LIMIT';
    } else if (error.message?.includes('network')) {
      enhanced.message = 'Network error. Please check your internet connection.';
      enhanced.code = 'NETWORK_ERROR';
    } else if (!error.message) {
      enhanced.message = 'An unknown error occurred. Please try again.';
      enhanced.code = 'UNKNOWN_ERROR';
    }
    
    // Always return an object with content field for consistency
    enhanced.content = enhanced.message;
    
    return enhanced;
  }
  
  /**
   * Rate limit check
   */
  async rateLimitCheck() {
    const now = Date.now();
    const windowStart = now - this.rateLimit.window;
    
    // Clean old requests
    this.rateLimit.queue = this.rateLimit.queue.filter(t => t > windowStart);
    
    // Check if we're at limit
    if (this.rateLimit.queue.length >= this.rateLimit.requests) {
      const oldestRequest = this.rateLimit.queue[0];
      const waitTime = oldestRequest + this.rateLimit.window - now;
      
      console.log(`⏳ Rate limit reached. Waiting ${Math.ceil(waitTime / 1000)}s...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    // Add current request
    this.rateLimit.queue.push(now);
  }
  
  /**
   * Get cache key
   */
  getCacheKey(prompt, options) {
    // Include prompt identifier + version to avoid stale cache after edits
    const key = `${options.promptKey || 'unknown'}:${options.promptVersion || 'v?'}:${options.model}:${options.temperature}`;
    let hash = 0;
    for (let i = 0; i < prompt.length; i++) {
      const ch = prompt.charCodeAt(i);
      hash = ((hash << 5) - hash) + ch;
      hash |= 0; // 32-bit
    }
    return `${key}:${prompt.length}:${hash.toString(16)}`;
  }
  
  /**
   * Clean expired cache entries
   */
  cleanCache() {
    for (const [key, entry] of this.cache.entries()) {
      if (!entry.isValid()) {
        this.cache.delete(key);
      }
    }
  }
  
  /**
   * Cancel active request
   */
  cancelRequest(requestId) {
    const controller = this.activeRequests.get(requestId);
    if (controller) {
      controller.abort();
      this.activeRequests.delete(requestId);
    }
  }
  
  /**
   * Add interceptor for extending functionality
   */
  addInterceptor(interceptor) {
    this.interceptors.push(interceptor);
  }
  
  /**
   * Get service statistics
   */
  getStats() {
    return {
      totalRequests: this.requestCount,
      activeRequests: this.activeRequests.size,
      cacheSize: this.cache.size,
      cacheHitRate: this.calculateCacheHitRate(),
      rateLimitStatus: {
        used: this.rateLimit.queue.length,
        limit: this.rateLimit.requests
      },
      hasApiKey: !!this.apiKey
    };
  }
  
  /**
   * Test the API connection
   */
  async testConnection() {
    try {
      const response = await this.call('Say "OK"', {
        maxTokens: 10,
        temperature: 0
      });
      return response?.content?.includes('OK');
    } catch (error) {
      console.error('API test failed:', error);
      return false;
    }
  }
  
  calculateCacheHitRate() {
    // This would track hits vs misses in production
    return 0;
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export default (ApiService class is already exported at the top)
export default apiService;

// Attach to window in development
if (import.meta.env.DEV) {
  window.ApiService = apiService;
  console.log('🔧 ApiService available at window.ApiService');
  
  // Test function
  window.testApi = async (prompt = 'Say hello') => {
    try {
      const response = await apiService.call(prompt);
      console.log('API Response:', response);
      return response;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };
  
  console.log('💡 Test with: await window.testApi("Your prompt here")');
}
// @ts-check
